/**
 * App-owned user preferences — the source of truth for app.emdr.link settings
 * ONLY (session defaults, UI prefs, device config). WordPress-owned profile
 * fields (directory visibility, photo, courses) do NOT live here; read/write
 * them through the composite profile facade below.
 *
 * API contract:
 *   GET  /users/preferences                 -> { [namespace]: {..} }   (app-owned)
 *   PUT  /users/preferences/:namespace      -> merged namespace         (app-owned)
 *        body: { values: {..} }; null-valued keys delete.
 *   GET  /users/profile/composite           -> unified profile federated across
 *                                              Auth0 + app + WordPress
 *   PUT  /users/profile/field/:field        -> routes the write to the field's
 *        body: { value }                      owning system (no sync, no copy)
 *
 * Standalone functions share a module-level cache so non-hook call sites
 * (redux handlers, plain components) stay in sync with the usePreferences() hook.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../components/api/api';

export type Namespace = string;
export type PreferencesMap = Record<Namespace, Record<string, any>>;

const cache: PreferencesMap = {};
let inflight: Promise<PreferencesMap> | null = null;

export async function fetchPreferences(): Promise<PreferencesMap> {
  if (!inflight) {
    inflight = api()
      .get('/users/preferences')
      .then((res): PreferencesMap => {
        Object.assign(cache, res.data ?? {});
        return cache;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export async function saveNamespace(
  ns: Namespace,
  values: Record<string, any>,
): Promise<Record<string, any>> {
  const merged = await api()
    .put(`/users/preferences/${ns}`, { values })
    .then((res) => res.data as Record<string, any>);
  cache[ns] = merged;
  return merged;
}

/**
 * Unified therapist profile, federated server-side across its owning sources
 * (Auth0 identity + app + WordPress directory). Use this to READ fields whose
 * owner may be WordPress (e.g. searchVisible, photoUrl) — never assume they
 * live in the app preferences bag.
 */
export async function fetchCompositeProfile(): Promise<{
  profile: Record<string, any>;
  directory: Record<string, any> | null;
  preferences: PreferencesMap;
  sources: Record<string, any>;
}> {
  return api()
    .get('/users/profile/composite')
    .then((res) => res.data);
}

/**
 * Update a single profile field. The API routes it to its owning system
 * (app -> Mongo, wordpress -> WP's existing REST API). This is how the app
 * edits WordPress-owned fields without storing a second copy or touching WP.
 */
export async function saveProfileField(field: string, value: any): Promise<any> {
  return api()
    .put(`/users/profile/field/${field}`, { value })
    .then((res) => res.data?.value);
}

export function usePreferences() {
  const { isAuthenticated } = useAuth0();
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    setLoading(true);
    fetchPreferences().finally(() => mounted.current && setLoading(false));
  }, [isAuthenticated]);

  const getNamespace = useCallback(async (ns: Namespace): Promise<Record<string, any>> => {
    await fetchPreferences();
    return cache[ns] ?? {};
  }, []);

  return { preferences: cache, loading, getNamespace, saveNamespace };
}
